import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BACKEND_URL } from '../../utils/config';
import { logout } from '../slices/authSlice';

// Create a custom base query with error handling
const baseQueryWithReauth = fetchBaseQuery({
  baseUrl: BACKEND_URL,
  credentials: 'include',
  prepareHeaders: (headers, { getState, endpoint, extraOptions }) => {
    // Identify public endpoints based on URL pattern
    if (isPublicEndpoint(endpoint)) {
      console.log('Public endpoint detected:', endpoint);
      // For public endpoints, we don't need to add authentication headers
      return headers;
    }

    const token = getState()?.auth?.token || localStorage.getItem('token');
    
    // Add auth header if token exists
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    
    // Ensure proper content-type for JSON requests (if not FormData)
    if (!(extraOptions?.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    
    return headers;
  }
});

const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
  // For FormData, make sure we don't mess with the Content-Type
  // The browser will set it automatically with the correct boundary
  if (args.body instanceof FormData) {
    // Remove any existing Content-Type header to let the browser set it
    if (args.headers) {
      delete args.headers['Content-Type'];
    }
  }

  try {
    const result = await baseQueryWithReauth(args, api, extraOptions);
    
    if (result.error) {
      // Check if this is a public endpoint (based on URL pattern)
      const isPublicEndpointRequest = args.url && isPublicEndpoint(args.url);
      
      // Handle authentication errors (except for public endpoints)
      if (result.error.status === 401 && !isPublicEndpointRequest) {
        // Auth error - clear credentials
        api.dispatch(logout());
      }
    }
    
    return result;
  } catch (error) {
    console.error('API request failed:', error);
    return { error: { status: 'FETCH_ERROR', error: error.message } };
  }
};

export const createBaseApi = (reducerPath) => {
  return createApi({
    reducerPath,
    baseQuery: baseQueryWithErrorHandling,
    tagTypes: ['Auth', 'User', 'Product', 'Listing', 'Review', 'Profile', 'Address', 'Upload', 'Cart', 'Chats', 'Messages', 'UnreadCount', 'Merchant', 'Order', 'FulfillmentGroup', 'OrderItem', 'Request', 'Delivery', 'UserReviewsGiven', 'UserReviewsReceived', 'ExistingCustomerReview', 'ExistingOrderCustomerReview'],
    endpoints: () => ({}),
    keepUnusedDataFor: 5 * 60, // Keep unused data in cache for 5 minutes
    refetchOnMountOrArgChange: 30, // Refetch after 30 seconds
    refetchOnFocus: false,
    refetchOnReconnect: true
  });
};

export const createProtectedEndpoint = (builder, endpoint) => {
  return builder.query({
    ...endpoint,
    transformErrorResponse: (response) => {
      if (response.status === 401) {
        // Handle unauthorized access
        return { error: 'Unauthorized access' };
      }
      return response;
    }
  });
};

export const handleWebhookUpdate = (store, tags = ['Product']) => {
  tags.forEach(tag => {
    Object.values(store.dispatch)
      .filter(value => typeof value === 'object' && value.util)
      .forEach(api => {
        store.dispatch(api.util.invalidateTags([tag]));
      });
  });
};

// List of endpoints that don't require authentication
const isPublicEndpoint = (endpoint) => {
  return (
    endpoint.includes('login') ||
    endpoint.includes('register') ||
    endpoint.includes('forgot-password') ||
    endpoint.includes('reset-password') ||
    endpoint.includes('social-auth-url') ||
    endpoint.includes('verify-email') ||
    endpoint.includes('like-status')
  );
};
